import { fabric } from 'fabric';
import { type AttachmentPoint, ConnectorEnd } from '../types';
import CollaborationBehaviors from './CollaborationBehaviors';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import tools from '../tools';
import ConnectorPointControl from '../controls/ConnectorPointControl';
import ConnectorSplitControl from '../controls/ConnectorSplitControl';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import RouterStraight from './connectorRouters/RouterStraight';
import RouterBezier from './connectorRouters/RouterBezier';
import RouterOrthogonal from './connectorRouters/RouterOrthogonal';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import * as Sentry from '@sentry/browser';
import LockBehaviors from './LockBehaviors';
import { CONNECTOR_DEFAULT_WIDTH } from '../utils/DefaultsManager';
import type LabelBehaviors from './LabelBehaviors';
import type ShapeDrawingBrush from '../stylus/ShapeDrawingBrush';
import type EmbeddedTextBehaviors from './EmbeddedTextBehaviorsV2';
import { type ToolList } from 'fabric/ObjectBehaviors';
import StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import Shape from './Shape';
import { type Point, type IPoint } from 'fabric/fabric-impl';
import { hasConnectorAttachmentBehaviours } from './guards';
import type WhiteboardController from '../controllers/WhiteboardController';
import Debug from 'debug';
import {
  type ConnectorEndStyle,
  CONNECTOR_END_STYLE,
  COLORS,
  STROKE_TYPES,
} from '../constants';
import type ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import { ConnectorRouting, type TConnectorRouting } from './ConnectorRouting';

const debug = Debug('whiteboard');

// How close the cursor needs to be to a connector line to select it.
const SELECTION_DISTANCE = 10;

export type LabelObject = fabric.Object &
  LabelBehaviors &
  EmbeddedTextBehaviors;

export type Attachable = fabric.Object & ConnectorAttachmentBehaviors;
export interface Attachment {
  object: Attachable;
  attachmentPoint: AttachmentPoint;
  normal: IPoint;
  edgeOffset?: number; // For edge attachments, position along perimeter (0.0-1.0)
}

const hollowFill = (ctx: CanvasRenderingContext2D) => {
  // use the same colour as the canvas background to make it look unfilled, without showing anything behind it
  ctx.fillStyle = COLORS['--theme-tertiary-background'];
  ctx.fill();
};

export type ConnectorOptions = Pick<
  Connector,
  | 'attachments'
  | 'controller'
  | 'dependencyLineInfo'
  | 'drawing'
  | 'locked'
  | 'lineRouting'
  | 'strokeType'
  | 'stroke'
  | 'strokeWidth'
> & {
  controller: WhiteboardController;
};

// A line or connector between shapes
interface Connector
  extends fabric.Object,
    LockBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors {}

@LockBehaviors
@AnalyticsBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
class Connector extends StyleCopyPasteBehaviors(fabric.Object) {
  static DEFAULT_WIDTH = CONNECTOR_DEFAULT_WIDTH;

  type = 'connector';

  noScaleCache = false;
  objectCaching = false;

  strokeUniform = true;
  transparentCorners = false;
  hasBorders = false;

  // Array of ShapeText objects attached to this connector, cached only
  labels: LabelObject[] = [];
  // Alignment of labels, setting angle to conform to line or 0
  alignLabels = false;

  // Array of {x,y} points making up the connector line.
  points: fabric.IPoint[];
  // Temporary copy of the original points, used when the connector is being transformed.
  _originalPoints?: fabric.IPoint[];
  // Array of {x,y} points controlling the shape of the line between points.
  controlPoints: IPoint[];
  // Offset to the center of the path.
  pathOffset: fabric.Point;
  // Objects attached at the start and end of the connector. We cache the
  // normal vector for the attachment for performance. {object,attachmentPoint,normal}
  attachments: (Attachment | null)[];

  // Type of arrow on each end of the connector. One of the
  // CONNECTOR_END_STYLE.* constants.
  endStyles: ConnectorEndStyle[];
  // Line routing style for the connector. Once of the ConnectorRouting.ROUTING_*
  // constants.
  lineRouting: TConnectorRouting | null = null;

  // Type of the line. One of the STROKE_TYPE_* constants from ToolStrokeStyle
  strokeType: number;

  cacheProperties = (fabric.Object.prototype.cacheProperties ?? []).concat(
    'points'
  );

  // Fix to prevent dblclick events creating a label in undesirable
  // sitatuations. Ideal solution would adjust double click delay
  mouseDown = false;

  // Whether this connector is currently being drawn with the connector drawing brush.
  drawing = false;

  // Whether this connector is currently being adjusted with a control.
  beingControlled = false;

  // If this connector is a dependency line, save the record info
  dependencyLineInfo: {
    parentRecordId: string;
    childRecordId: string;
    linkType: string;
  } | null = null;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'lineRouting',
    'strokeType',
    'endStyles',
    'points',
    'attachments',
    'stroke',
    'strokeWidth',
    'container',
    'alignLabels',
    'dependencyLineInfo',
  ];

  serializedAttachments: any;

  /** Handles the repeated work of creating a new connector and adding it to the canvas.
   * This method should be used instead of using new Connector directly for persisted connectors.
   *  */
  static create(points: IPoint[], options: ConnectorOptions) {
    return options.controller.addToCanvas(new Connector(points, options));
  }

  constructor(points: IPoint[], options: ConnectorOptions) {
    super({});

    this.cornerStyle = 'circle';
    this.fill = 'transparent';
    this.strokeLineCap = 'round';
    this.strokeLineJoin = 'round';
    this.drawing = options.drawing;
    this.dependencyLineInfo = options.dependencyLineInfo;

    const defaults = {
      points: points || [],
      serializedAttachments: options.attachments,
    };
    this.attachments = options.attachments = [null, null];
    this.points = [];
    this.controlPoints = [];

    this.initializeLockBehaviors(options.locked ?? false);

    super.setOptions(Object.assign(defaults, options));

    this.updateCursor();

    this.endStyles = this.endStyles || [
      CONNECTOR_END_STYLE.NONE,
      CONNECTOR_END_STYLE.FILLED_ARROW,
    ];
    this.strokeType = this.strokeType || STROKE_TYPES.SOLID;
    this._setPositionDimensions();
    this._computeControlPoints();
    this.createControls();
    this.initializeFrameMember();

    this.on('scaling', e => this.constrainConnector(e));
    this.on('moving', e => {
      this.mouseDown = false;
      this.constrainConnector(e);
    });
    this.on('added', () => {
      if (this.serializedAttachments) {
        this.rehydrateAttachments(this.serializedAttachments);
        delete this.serializedAttachments;
      }
    });

    this.on('mousedown', () => {
      debug('Connector mousedown');
      this.mouseDown = true;
    });

    this.on('mousedblclick', e => {
      debug('Connector mousedblclick');
      // Ignore dblclicks that happen after connector drag or when clicking
      // connector control

      if (
        !this.mouseDown ||
        e.target?.__corner !== 0 ||
        this.locked ||
        this.canvas.readOnly ||
        !this.controller.props.allowUpdate
      )
        return;

      this.mouseDown = false;
      const { absolutePointer } = e;
      const offset = this.router?.closestPointOnPath(this, absolutePointer!);
      this.createLabel({ offset });
    });

    this.on('mouseup', () => {
      if (this.numValidAttachments === 1 && this.drawing) {
        this.canvas.controller?.actionShowConnectorShapeMenu();
      }
      this.drawing = false;
    });
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['endStyles', 'stroke', 'strokeWidth', 'strokeType'];
  }

  // When adding labels via toolbar, choose largest gap to insert new label
  createLabelWithCalculatedOffset() {
    let offset = 0.5;
    if (this.labels.length > 0) {
      let maxDiff = 0;
      // Insert new label at midpoint of largest gap between current labels
      let lastOffset = 0;
      const sortedLabels = [...this.labels]
        .map(label => label.labelPosition ?? 0)
        .sort((l1, l2) => l1 - l2);
      for (let i = 0; i < sortedLabels.length; i++) {
        const l1 = sortedLabels[i];
        if (l1 - lastOffset > maxDiff) {
          maxDiff = l1 - lastOffset;
          offset = (l1 + lastOffset) / 2;
        }
        lastOffset = l1;
      }
      // Check against space between last label and end of connector
      if (1 - lastOffset > maxDiff) {
        offset = (1 + lastOffset) / 2;
      }
    }
    this.createLabel({ offset });
  }

  createIconLabel({
    iconName,
    offset = 0.5,
  }: {
    iconName: string;
    offset?: number;
  }) {
    const shape = Shape.create({
      controller: this.controller,
      shapeName: 'icon',
      iconName,
      scaleX: 0.2,
      scaleY: 0.2,
      fill: this.stroke,
    });
    shape.initLabel(this, offset);
  }

  createLabel(options?: {
    offset?: number;
    labelText?: string;
    width?: number;
  }) {
    const offset = options?.offset ?? 0.5;
    // Manually create shapeText
    this.canvas.controller.stylusText.activate();
    (this.canvas.freeDrawingBrush as ShapeDrawingBrush).createShape({
      drawing: false,
    });

    // Select created shape
    this.canvas.controller.stylusPointer.activate();
    const label = this.canvas.getActiveObject() as LabelObject;

    // Explicitly set dimensions to avoid positioning errors. ShapeText measures
    // dimensions async in EmbeddedTextBehaviors, so initial dimensions are 0
    label.set({ height: 30, width: options?.width ?? 30 });

    if (options?.labelText) {
      label.set('textContent', options.labelText);
    }

    try {
      label.initLabel(this, offset);
    } catch (error) {
      Sentry.captureException(error);
    }
    if (!options?.labelText) {
      void label.startTextEditing();
    }
  }

  addLabel(label: LabelObject) {
    if (!this.labels.find(l => l.id === label.id)) {
      this.labels.push(label);
    }
  }

  // Remove an individual label from this connector's labels
  removeLabel(label: LabelObject) {
    this.labels = this.labels.filter(l => l.id !== label.id);
  }

  removeLabels() {
    while (this.labels.length > 0) {
      const label = this.labels.pop() as LabelObject;
      this.deleteLabel(label);
    }
  }

  deleteLabel(label: LabelObject) {
    label.deleted(this.controller);
    this.canvas?.remove(label);
  }

  toggleLabelAlignment() {
    this.set('alignLabels', !this.alignLabels);
    this._updateLabels();
    this.canvas?.requestRenderAll();
  }

  // Detach from objects when deleted.
  deleted() {
    this.removeLabels();
    this.attachments[ConnectorEnd.CONNECTOR_START]?.object?.detachConnector(
      this
    );
    this.attachments[ConnectorEnd.CONNECTOR_END]?.object?.detachConnector(this);
  }

  synchronize() {
    this.constrainConnector({});
  }

  // Constrain the ends of the connector to their attachment points.
  constrainConnector(eventData) {
    // When moving the connector itself, ensure that attach endpoints stay
    // in place (potentially preventing the connector from moving at all).
    this.attachments[
      ConnectorEnd.CONNECTOR_START
    ]?.object?.constrainConnectorEnd(
      eventData,
      this,
      ConnectorEnd.CONNECTOR_START
    );
    this.attachments[ConnectorEnd.CONNECTOR_END]?.object?.constrainConnectorEnd(
      eventData,
      this,
      ConnectorEnd.CONNECTOR_END
    );
  }

  // Create the control handles for the connector.
  createControls() {
    this.controls = {};

    // Drag controls.
    for (let index = 0; index < this.points.length; index++) {
      if (
        (this.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO ||
          this.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL) &&
        ![0, this.points.length - 1].includes(index)
      ) {
        continue;
      }
      this.controls[`p${index}`] = new ConnectorPointControl(
        this,
        index,
        this.points.length
      );
    }

    // Split controls.
    if (this.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO) {
      for (let index = 0; index < this.controlPoints.length - 1; index++) {
        this.controls[`s${index}`] = new ConnectorSplitControl(this, index);
      }
    } else {
      for (let index = 0; index < this.points.length - 1; index++) {
        this.controls[`s${index}`] = new ConnectorSplitControl(this, index);
      }
    }
  }

  tools() {
    const connectorTools: ToolList = [
      [tools.ToolConnectorType],
      [tools.ToolStrokeColor, tools.ToolStrokeStyle],
      [tools.ToolConnectorStartArrow, tools.ToolConnectorEndArrow],
      [tools.ToolAddLabel, tools.ToolToggleLabelAlignment],
      [tools.ToolCreateRecordLink],
    ];

    return connectorTools;
  }

  // Compute the midpoint between the two points at afterIndex.
  midPoint(afterIndex: number) {
    return this.router.midPoint(this, afterIndex);
  }

  // Return the coordinates of the end of the connector.
  endPoint(whichEnd: ConnectorEnd) {
    if (whichEnd === ConnectorEnd.CONNECTOR_START) {
      return this.points[0];
    } else {
      return this.points[this.points.length - 1];
    }
  }

  // Return the coordinates of the second to last point in the connector, or the
  // last point if there are not intermediate points.
  penultimatePoint(whichEnd: ConnectorEnd) {
    if (this.points.length > 2) {
      if (whichEnd === ConnectorEnd.CONNECTOR_START) {
        return this.points[1];
      } else {
        return this.points[this.points.length - 2];
      }
    } else {
      return this.endPoint(Connector.otherEnd(whichEnd));
    }
  }

  // Add a new point to the connector, after the point at afterIndex.
  addPointAfter(afterIndex: number) {
    const newPoints = [
      ...this.points.slice(0, afterIndex + 1),
      this.midPoint(afterIndex),
      ...this.points.slice(afterIndex + 1),
    ];
    this.set('points', newPoints);
    this.createControls();
    this.canvas.requestRenderAll();
  }

  removePointAtIndex(index: number) {
    const newPoints = this.points.filter((_, i) => i !== index);
    this.set('points', newPoints);
    this.createControls();
    this.canvas.requestRenderAll();
  }

  _pointNearLineSegment(point: Point, index: number, radius: number) {
    return this.router.pointNearLineSegment(this, point, index, radius);
  }

  // Override default behavior for selection tracking to only allow line to
  // be selected by clicking on it, rather than selecting by clicks on the
  // bounding box.
  containsPoint(pointer: Point) {
    // Translate the absolute mouse position to the viewport.
    let adjustedPointer = this.canvas.restorePointerVpt(pointer);
    // Translate again to the object coordinates.
    adjustedPointer = fabric.util.transformPoint(
      adjustedPointer,
      fabric.util.invertTransform(this.calcOwnMatrix())
    );
    adjustedPointer.x += this.pathOffset.x;
    adjustedPointer.y += this.pathOffset.y;
    const radius = SELECTION_DISTANCE / this.canvas.getZoom();

    // Quick check using bounding box since the algorithm below is slow.
    if (!this._boundingBoxContainsPoint(adjustedPointer, radius)) {
      return false;
    }

    for (let i = 0, len = this.points.length - 1; i < len; i++) {
      if (this._pointNearLineSegment(adjustedPointer, i, radius)) {
        return true;
      }
    }
    return false;
  }

  // Assumes pointer is already in object coordinate space
  nearPoint(pointer: Point) {
    const radius = SELECTION_DISTANCE + 17.5 / this.canvas.getZoom();

    // Quick check using bounding box since the algorithm below is slow.
    if (!this._boundingBoxContainsPoint(pointer, radius)) {
      return false;
    }

    for (let i = 0, len = this.points.length - 1; i < len; i++) {
      if (this._pointNearLineSegment(pointer, i, radius)) {
        return true;
      }
    }
    return false;
  }

  setFromRemote(key: string, value: unknown) {
    if (key === 'attachments') {
      if (this.canvas) {
        this.rehydrateAttachments(value);
      } else {
        this.serializedAttachments = value;
      }
    } else {
      super.setFromRemote(key, value);
    }

    if (key === 'points') {
      this._setPositionDimensions();
      this._computeControlPoints();
      this.createControls();
    }
    this.constrainConnector({});
  }

  rehydrateAttachments(attachmentsJSON: unknown) {
    const hydrate = (attachmentJSON: any, whichEnd: ConnectorEnd) => {
      if (attachmentJSON?.objectId) {
        const obj = this.controller.dataStoreObjects.findRecordById(
          attachmentJSON.objectId
        );
        if (!obj || !hasConnectorAttachmentBehaviours(obj)) {
          console.log(
            `Unable to find object ${attachmentJSON.objectId} to hydrate connector attachment`
          );
          return;
        }
        const { attachmentPoint, edgeOffset } = attachmentJSON;
        obj.attachConnector(this, whichEnd, attachmentPoint, edgeOffset);
      } else {
        // Detach if the connector was attached.
        this.attachments[whichEnd]?.object?.detachConnector(this);
        this.attachments[whichEnd] = null;
      }
    };

    if (
      !attachmentsJSON ||
      !Array.isArray(attachmentsJSON) ||
      attachmentsJSON.length !== 2
    ) {
      return;
    }

    hydrate(attachmentsJSON[0], ConnectorEnd.CONNECTOR_START);
    hydrate(attachmentsJSON[1], ConnectorEnd.CONNECTOR_END);

    this.updateCursor();
    this.constrainConnector({});
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    // We ignore scaling changes when the connector is scaled as part of a group.
    if (
      typeof keyOrPath === 'string' &&
      ['scaleX', 'scaleY'].includes(keyOrPath)
    )
      return this;

    if (keyOrPath === 'attachments' && this.attachments) {
      value.forEach((attachment, whichEnd) => {
        // Attachment may have a different object, so detach existing and attach new
        // May be null and we need to detach existing
        if (attachment) {
          let object;
          if (attachment.object) {
            object = attachment.object;
          } else if (attachment.objectId) {
            object = this.controller.dataStoreObjects.findRecordById(
              attachment.objectId
            );
          }

          if (object !== this.attachments[whichEnd]?.object) {
            const { attachmentPoint, edgeOffset } = attachment;
            object.addConnector(this, whichEnd, attachmentPoint, edgeOffset);
          }
        } else {
          this.attachments[whichEnd]?.object?.detachConnector(this);
        }
      });
      this.updateCursor();
    }

    // Handle the whole connector being moved for unattached connectors. Update
    // the points to stay in sync with the location of the connector.
    if (typeof keyOrPath === 'string' && ['left', 'top'].includes(keyOrPath)) {
      this.set({
        points: this.points.map(point => {
          if (keyOrPath === 'left') {
            return { x: point.x + (value - this.left), y: point.y };
          } else {
            return { x: point.x, y: point.y + (value - this.top) };
          }
        }),
      });
      this._setPositionDimensions();

      return this;
    }

    super._set(keyOrPath, value);

    // Recompute control points when points change.
    if (keyOrPath === 'points') {
      this._computeControlPoints();
    }

    // Recreate controls when routing changes.
    if (keyOrPath === 'lineRouting') {
      this.createControls();
      this._computeControlPoints();
    }

    // Adjust dimensions when strokeWidth set to reflect changes in real time
    if (keyOrPath === 'strokeWidth') {
      this._setPositionDimensions();
    }

    return this;
  }

  // TODO: memoize the bounding box.
  _boundingBoxContainsPoint(point: Point, radius: number) {
    // Construct the bounding box containing all points and control points.
    const tl = { x: this.points[0].x, y: this.points[0].y };
    const br = { x: this.points[0].x, y: this.points[0].y };

    for (let i = 1; i < this.points.length; i++) {
      const p = this.points[i];
      if (p.x < tl.x) tl.x = p.x;
      if (p.x > br.x) br.x = p.x;
      if (p.y < tl.y) tl.y = p.y;
      if (p.y > br.y) br.y = p.y;
    }

    if (this.controlPoints) {
      for (let i = 0; i < this.controlPoints.length; i++) {
        const p = this.controlPoints[i];
        if (!p) continue;
        if (p.x < tl.x) tl.x = p.x;
        if (p.x > br.x) br.x = p.x;
        if (p.y < tl.y) tl.y = p.y;
        if (p.y > br.y) br.y = p.y;
      }
    }
    // Outset by the radius
    tl.x -= radius;
    tl.y -= radius;
    br.x += radius;
    br.y += radius;

    return point.x > tl.x && point.y > tl.y && point.x < br.x && point.y < br.y;
  }

  // When checking if the center of the canvas is in the object (to see if
  // object is on the screen), use the inherited bounding box implementation.
  _containsCenterOfCanvas(pointTL: Point, pointBR: Point, calculate: boolean) {
    // worst case scenario the object is so big that contains the screen
    const centerPoint = new fabric.Point(
      (pointTL.x + pointBR.x) / 2,
      (pointTL.y + pointBR.y) / 2
    );
    if (super.containsPoint(centerPoint, null, true, calculate)) {
      return true;
    }
    return false;
  }

  // Attach an object to CONNECTOR_START or CONNECTOR_END of this connector.
  // Also specify the normal vector at the attachment point (i.e. the direction
  // the connector should depart from the object).
  attachObject(
    whichEnd: ConnectorEnd,
    object: Attachable,
    attachmentPoint: AttachmentPoint,
    normal: IPoint,
    edgeOffset?: number
  ) {
    const newAttachments = [...this.attachments];
    // If we were already attached, detach that object.
    newAttachments[whichEnd]?.object?.detachConnector(this);
    newAttachments[whichEnd] = {
      object,
      attachmentPoint,
      normal,
      edgeOffset,
    };
    this.set('attachments', newAttachments);
  }

  detachObjectAtIndex(index: number) {
    const newAttachments = [...this.attachments];
    if (index === 0) {
      newAttachments[ConnectorEnd.CONNECTOR_START]?.object?.detachConnector(
        this
      );
      newAttachments[ConnectorEnd.CONNECTOR_START] = null;
      this.set('attachments', newAttachments);
    } else if (index === this.points.length - 1) {
      newAttachments[ConnectorEnd.CONNECTOR_END]?.object?.detachConnector(this);
      newAttachments[ConnectorEnd.CONNECTOR_END] = null;
      this.set('attachments', newAttachments);
    }
  }

  detachObject(object: Attachable | undefined) {
    const newAttachments = [...this.attachments];
    if (newAttachments[ConnectorEnd.CONNECTOR_START]?.object === object) {
      newAttachments[ConnectorEnd.CONNECTOR_START] = null;
      this.set('attachments', newAttachments);
    }
    if (newAttachments[ConnectorEnd.CONNECTOR_END]?.object === object) {
      newAttachments[ConnectorEnd.CONNECTOR_END] = null;
      this.set('attachments', newAttachments);
    }
  }

  attached() {
    return (
      this.attachments[ConnectorEnd.CONNECTOR_START] ||
      this.attachments[ConnectorEnd.CONNECTOR_END]
    );
  }

  // If the connector is attached, it can't be moved. Only the control points
  // can be moved in that case.
  private updateCursor() {
    if (this.numValidAttachments > 0) {
      this.hoverCursor = 'default';
    } else {
      this.hoverCursor = undefined;
    }
  }

  calcDimensions() {
    const min = fabric.util.array.min;
    const max = fabric.util.array.max;

    const points =
      this.lineRouting === ConnectorRouting.ROUTING_BEZIER
        ? this.points
        : this.controlPoints;
    const minX = min(points, 'x') || 0;
    const minY = min(points, 'y') || 0;
    const maxX = max(points, 'x') || 0;
    const maxY = max(points, 'y') || 0;
    const width = maxX - minX;
    const height = maxY - minY;

    return {
      left: minX,
      top: minY,
      width,
      height,
    };
  }

  // Set the position and dimensions of the containing object.
  _setPositionDimensions() {
    const calcDim = this.calcDimensions();
    this.left = calcDim.left - this.strokeWidth / 2;
    this.top = calcDim.top - this.strokeWidth / 2;
    this.width = calcDim.width;
    this.height = calcDim.height;

    // Offset to the center of the path, used when rendering.
    this.pathOffset = {
      x: calcDim.left + calcDim.width / 2,
      y: calcDim.top + calcDim.height / 2,
    } as fabric.Point;
  }

  // Render the line.
  _render(ctx: CanvasRenderingContext2D) {
    // Skip rendering if it would be too small to see.
    if (this.strokeWidth * this.canvas.getZoom() < 0.12) return;

    this.fill = this.stroke;

    ctx.save();
    this.labels.length > 0 && this._clipLabels(ctx);
    ctx.beginPath();
    this._setConnectorLineDash(ctx);
    this._drawLine(ctx);
    this._renderStroke(ctx);

    // Draw the arrows on each end.
    this._setConnectorLineDash(ctx, STROKE_TYPES.SOLID);
    this._drawArrowHead(ctx, ConnectorEnd.CONNECTOR_START);
    this._drawArrowHead(ctx, ConnectorEnd.CONNECTOR_END);

    ctx.restore();
  }

  get router() {
    switch (this.lineRouting) {
      case ConnectorRouting.ROUTING_STRAIGHT:
        return RouterStraight;

      case ConnectorRouting.ROUTING_BEZIER:
        return RouterBezier;

      default:
        return RouterOrthogonal;
    }
  }

  // Compute the control points for the line. This must be done each time the
  // points might change.
  _computeControlPoints() {
    this.router.computeControlPoints(this);
    this._updateLabels();
  }

  // Update position of labels as line is recomputed
  _updateLabels() {
    // After computing new control points, let labels know they should
    // update the relevant values
    if (this.labels.length > 0) {
      this.labels.forEach(label => {
        label.updateLabelPositionAndAngle(this);
      });
    }
  }

  // Add clipping for labels by clipping the label and the connector,
  // removing any place they overlap
  _clipLabels(ctx: CanvasRenderingContext2D) {
    const offsetX = this.pathOffset.x;
    const offsetY = this.pathOffset.y;
    const minDimension = 100;

    ctx.beginPath();
    // Apply clip for each label
    this.labels.forEach(label => {
      const { left, top, angle } = label;
      const scaledHeight = label.getScaledHeight();
      const scaledWidth = label.getScaledWidth();

      if (this.alignLabels) {
        // This block works for both aligned and non-aligned labels, but is more
        // expensive
        const adjustedPointer = fabric.util.transformPoint(
          { x: left, y: top } as fabric.Point,
          fabric.util.invertTransform(this.calcOwnMatrix())
        );
        ctx.save();
        ctx.translate(adjustedPointer.x, adjustedPointer.y);
        ctx.rotate(fabric.util.degreesToRadians(angle as number));
        ctx.translate(-adjustedPointer.x, -adjustedPointer.y);
        ctx.rect(
          adjustedPointer.x,
          adjustedPointer.y,
          scaledWidth,
          scaledHeight
        );
        ctx.restore();
      } else {
        ctx.rect(left - offsetX, top - offsetY, scaledWidth, scaledHeight);
      }
    });

    // Ensure that all line types (especially bezier) render within clip.
    ctx.rect(
      (-this.width - minDimension) * 2,
      (-this.height - minDimension) * 2,
      (this.width + minDimension) * 4,
      (this.height + minDimension) * 4
    );

    // Comment in for debugging, to see where clip box is drawn
    // ctx.stroke();

    ctx.clip('evenodd');
  }

  _drawLine(ctx) {
    this.router.drawLine(this, ctx);
  }

  _setConnectorLineDash(
    ctx: CanvasRenderingContext2D,
    strokeType = this.strokeType
  ) {
    switch (strokeType) {
      case STROKE_TYPES.SOLID:
        ctx.setLineDash([]);
        break;
      case STROKE_TYPES.DASHED:
        ctx.setLineDash([
          10 * Math.sqrt(this.strokeWidth),
          10 * Math.sqrt(this.strokeWidth),
        ]);
        break;
      case STROKE_TYPES.DOTTED:
        ctx.setLineDash([0.5, 8 * Math.sqrt(this.strokeWidth)]);
        break;
    }
  }

  // Draw an arrowhead.
  _drawArrowHead(ctx: CanvasRenderingContext2D, whichEnd: ConnectorEnd) {
    const x = this.pathOffset.x;
    const y = this.pathOffset.y;
    const line = this.router.arrowHeadLine(this, whichEnd);

    this._drawArrowHeadForPoints(
      ctx,
      whichEnd,
      line.from.x - x,
      line.from.y - y,
      line.to.x - x,
      line.to.y - y
    );
  }

  // Draw the arrow on the *end* of the line from x1 to x2.
  _drawArrowHeadForPoints(
    ctx: CanvasRenderingContext2D,
    whichEnd: ConnectorEnd,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) {
    // common size and positioning for simple end shapes
    const size = (5 * this.strokeWidth) / Connector.DEFAULT_WIDTH;
    const len = Math.hypot(x1 - x2, y1 - y2);
    const x = x2 - (size * (x2 - x1)) / len;
    const y = y2 - (size * (y2 - y1)) / len;

    const endStyle = this.endStyles[whichEnd];
    switch (endStyle) {
      case CONNECTOR_END_STYLE.NONE:
        break;

      case CONNECTOR_END_STYLE.POINTY_ARROW:
      case CONNECTOR_END_STYLE.FILLED_ARROW:
      case CONNECTOR_END_STYLE.ARROW:
        this._drawArrow(ctx, endStyle, x1, y1, x2, y2);
        break;

      case CONNECTOR_END_STYLE.FILLED_CIRCLE:
      case CONNECTOR_END_STYLE.CIRCLE: {
        ctx.beginPath();
        ctx.ellipse(x, y, size, size, 0, 0, 2 * Math.PI);
        ctx.closePath();
        if (endStyle === CONNECTOR_END_STYLE.FILLED_CIRCLE) {
          this._renderFill(ctx);
        } else {
          hollowFill(ctx);
        }
        this._renderStroke(ctx);
        break;
      }
      case CONNECTOR_END_STYLE.DIAMOND: {
        ctx.beginPath();
        ctx.moveTo(x, y - size);
        ctx.lineTo(x + size, y);
        ctx.lineTo(x, y + size);
        ctx.lineTo(x - size, y);
        ctx.lineTo(x, y - size);
        ctx.closePath();
        hollowFill(ctx);
        this._renderStroke(ctx);
        break;
      }
      case CONNECTOR_END_STYLE.SLASH: {
        ctx.beginPath();
        ctx.moveTo(x - size, y - size);
        ctx.lineTo(x + size, y + size);
        ctx.closePath();
        this._renderStroke(ctx);
        break;
      }

      default: {
        const exhaustiveCheck: never = endStyle;
        throw new Error(`Unknown arrowhead ${exhaustiveCheck}`);
      }
    }
  }

  _drawArrow(
    ctx: CanvasRenderingContext2D,
    endStyle: ConnectorEndStyle,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) {
    const transform = this.calcOwnMatrix();
    const scale = transform[0];
    const size = (10 * this.strokeWidth) / Connector.DEFAULT_WIDTH;
    const cospix = 0.866025404;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dy * dy + dx * dx);
    const headlen = size / scale;
    const sina = dy / length;
    const cosa = dx / length;
    const cosp = cosa * cospix - 0.5 * sina;
    const cosm = cosa * cospix + 0.5 * sina;
    const sinp = cosa * 0.5 + cospix * sina;
    const sinm = cospix * sina - cosa * 0.5;
    ctx.beginPath();
    ctx.moveTo(x2 - headlen * cosm, y2 - headlen * sinm);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x2 - headlen * cosp, y2 - headlen * sinp);
    if (endStyle !== CONNECTOR_END_STYLE.POINTY_ARROW) {
      ctx.closePath();
      if (endStyle === CONNECTOR_END_STYLE.FILLED_ARROW) {
        this._renderFill(ctx);
      } else if (endStyle === CONNECTOR_END_STYLE.ARROW) {
        hollowFill(ctx);
      }
    }
    this._renderStroke(ctx);
  }

  pointsToJSON() {
    const stroke = this.strokeWidth / 2;
    return this.points.map(p => ({
      x: p.x + this.left + this.width / 2 - (this.pathOffset?.x || 0) + stroke,
      y: p.y + this.top + this.height / 2 - (this.pathOffset?.y || 0) + stroke,
    }));
  }

  attachmentsToJSON() {
    const serializeAttachment = attachment => {
      if (!attachment) {
        return null;
      } else if (attachment.object) {
        return {
          objectId: attachment.object.uniqueId(),
          attachmentPoint: attachment.attachmentPoint,
          edgeOffset: attachment.edgeOffset,
        };
      } else {
        return attachment;
      }
    };

    return [
      serializeAttachment(this.attachments[ConnectorEnd.CONNECTOR_START]),
      serializeAttachment(this.attachments[ConnectorEnd.CONNECTOR_END]),
    ];
  }

  lock() {
    this.labels.forEach(label => (label.evented = false));
  }

  unlock() {
    this.labels.forEach(label => (label.evented = true));
  }

  static otherEnd(whichEnd: ConnectorEnd) {
    return ConnectorEnd.CONNECTOR_END - whichEnd;
  }

  // Returns the number of non-null attachments.
  get numValidAttachments(): number {
    return this.attachments.reduce(
      (acc, attachment) => (attachment ? acc + 1 : acc),
      0
    );
  }
}

export default Connector;
